import React, { useEffect, useState, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import { WebApiCalls } from '../../webApiCalls/WebApiCalls';
import { toast } from 'react-toastify';
import moment from 'moment';
import { useAuth } from '../../hooks/useAuth';
import { Col, Row, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import _ from 'lodash';
import viewIcon from '../../assets/images/icons/viewModalIcon.svg';
import delIcon from '../../assets/images/icons/icon-delete.svg';
import CommonModal from '../Modals/CommonModal';
import { useNavigate } from 'react-router-dom';
import decryptData from '../../helpers/Decryption';
import Papa from 'papaparse';

export const selectorStyles = {
    control: (provided, state) => ({
        ...provided,
        minHeight: '38px',
        height: '38px',
        borderColor: '#ced4da',
        boxShadow: 'none',
        borderRadius: '6px', // match input border radius
        backgroundColor: '#fff', // match input background
        fontSize: '16px', // match input font size
        // Remove outline on focus for consistency
        outline: 'none',
        '&:hover': {
            borderColor: '#86b7fe'
        }
    }),
    valueContainer: (provided, state) => ({
        ...provided,
        height: '38px',
        padding: '0 12px', // match input padding
        display: 'flex',
        alignItems: 'center'
    }),
    indicatorsContainer: (provided, state) => ({
        ...provided,
        height: '38px'
    }),
    input: (provided, state) => ({
        ...provided,
        margin: '0px',
        padding: '0px'
    }),
    placeholder: (provided, state) => ({
        ...provided,
        color: '#adb5bd', // match input placeholder color
        fontSize: '16px'
    }),
    menuList: (provided, state) => ({
        ...provided,
        fontSize: 14,
        color: '#a4a4a4'
    }),
    singleValue: (provided, state) => {
        const opacity = state.isDisabled ? 0.5 : 1;
        const transition = 'opacity 300ms';
        return { ...provided, opacity, transition, fontSize: '16px' };
    }
};

const RedemptionHistory = () => {
    const [redemptionData, setRedemptionData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [buttonDisable, setButtonDisable] = useState(true);
    const [showDelModal, setShowDelModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState({});
    const [showBulkDel, setShowBulkDel] = useState(false);
    const [commuterFilter, setCommuterFilter] = useState('');
    const [commuters, setCommuters] = useState([]);
    const [isCommuterSelected, setIsCommuterSelected] = useState(false);
    const [branches, setBranches] = useState([]);
    const [branchFilter, setBranchFilter] = useState('');
    const [isBranchSelected, setIsBranchSelected] = useState(false);

    const { getToken, user } = useAuth();
    const vendorId = user?._id;
    const navigate = useNavigate();

    const handleClose = () => {
        setShowDelModal(false);
        setShowBulkDel(false);
    };

    const handleDelete = async () => {
        const token = getToken();
        if (token && selectedItem?._id) {
            try {
                setLoading(true);
                const response = await new WebApiCalls().deleteRedemptionHistory(token, { _id: selectedItem?._id });
                if (response?.code === 200) {
                    toast.success('Redemption history is deleted successfully.');
                    fetchRedemptionHistory(pageSize);
                    setSelectedRows([]);
                } else {
                    toast.error('Oops...! Something went wrong');
                }
            } catch (error) {
                toast.error('An error occurred while deleting the redemption history.');
            } finally {
                setLoading(false);
                handleClose();
            }
        }
    };

    const fetchRedemptionHistory = useCallback(
        async (pageSize = 10, pageNo = 1, search = '', startDate = '', endDate = '', commuter = '', branch = '') => {
            const token = getToken();
            if (token) {
                setLoading(true);
                try {
                    const role = user?.role;
                    const commuterId = role === 'commuter' ? user?._id : commuter;
                    const branchId = branch ? branch : '';

                    let response;
                    if (role === 'commuter') {
                        response = await new WebApiCalls().getCommuterRedemptionHistory(token, commuterId, pageSize, pageNo);
                    } else {
                        response = await new WebApiCalls().getRedemptionHistory(token, pageSize, pageNo, search, vendorId, startDate, endDate, commuterId, branchId);
                    }

                    if (response && response?.code === 200) {
                        const decryptedData = await decryptData(response?.data);
                        setRedemptionData(decryptedData || []);
                        setTotalRecords(response?.totalRecords || 0);
                    }
                } catch (error) {
                    toast.error('Failed to fetch redemptions.');
                } finally {
                    setLoading(false);
                }
            }
        },
        [getToken, vendorId, user]
    );

    // Fetch all commuters for the dropdown
    useEffect(() => {
        if (user?.role !== 'commuter') {
            const fetchCommuters = async () => {
                const token = getToken();
                if (token) {
                    try {
                        const response = await new WebApiCalls().getAllCommuters(token, vendorId);
                        setCommuters(response?.data || []);
                    } catch (error) {
                        toast.error('Failed to fetch commuters.');
                    }
                }
            };
            fetchCommuters();
        }
    }, [getToken, vendorId, user]);

    // Fetch all branches for the dropdown
    useEffect(() => {
        if (user?.role !== 'commuter') {
            const fetchBranches = async () => {
                const token = getToken();
                if (token) {
                    try {
                        const res = await new WebApiCalls().getAllBranches(token, 1000, 0, '', '');
                        if (res?.code === 200) {
                            const decryptedData = await decryptData(res?.data);
                            setBranches(decryptedData || []);
                        }
                    } catch (error) {
                        toast.error('Failed to fetch branches.');
                    }
                }
            };
            fetchBranches();
        }
    }, [getToken, vendorId, user]);

    useEffect(() => {
        // Only fetch for selected commuter and branch, or all if none selected
        fetchRedemptionHistory(pageSize, 1, '', '', '', commuterFilter, branchFilter);
    }, [fetchRedemptionHistory, pageSize, commuterFilter, branchFilter]);

    const searchDebounce = _.debounce((text) => {
        fetchRedemptionHistory(pageSize, 1, text, '', '', commuterFilter, branchFilter);
    }, 2000);

    const deleteBulk = async () => {
        try {
            setLoading(true);
            const token = getToken();
            if (token && selectedRows.length > 0) {
                const response = await new WebApiCalls().deleteBulkRedemptionHistory(token, { _ids: selectedRows });
                if (response?.code === 200) {
                    toast.success('Selected records deleted successfully.');
                    fetchRedemptionHistory(pageSize);
                    setSelectedRows([]);
                } else {
                    toast.error('Failed to delete records.');
                }
            }
        } catch (error) {
            toast.error('An error occurred while deleting records.');
        } finally {
            setLoading(false);
        }
    };

    const handleRowSelected = (state) => {
        const selectedIds = state.selectedRows.map((item) => item._id);
        setSelectedRows(selectedIds);
        setButtonDisable(selectedIds.length === 0);
    };

    useEffect(() => {
        if (selectedRows && selectedRows.length > 0) {
            setButtonDisable(false);
        } else {
            setButtonDisable(true);
        }
    }, [selectedRows]);

    const columns = [
        {
            name: 'Reward Title',
            selector: (row) => {
                const title = row?.offer?.name || '';
                if (row?.offer?.status === 'inactive') {
                    return (
                        <span>
                            {title}{' '}
                            <span className="badge badge-danger" style={{ fontSize: 12, verticalAlign: 'middle', backgroundColor: 'red', textTransform: 'capitalize' }}>
                                {row?.offer?.status?.charAt(0).toUpperCase() + row?.offer?.status?.slice(1)}
                            </span>
                        </span>
                    );
                }
                return title;
            },
            sortable: true,
            center: true
        },
        {
            name: 'Branches',
            selector: (row) =>
                Array.isArray(row?.branches)
                    ? row.branches
                          .map((b) => b?.name)
                          .filter(Boolean)
                          .join(', ')
                    : row?.branch?.name || '',
            sortable: true,
            center: true
        },
        ...(user?.role !== 'commuter'
            ? [
                  {
                      name: 'TR Rewards Member',
                      selector: (row) => row?.commuter?.name || '',
                      sortable: true,
                      center: true
                  }
              ]
            : []),
        {
            name: 'Redemption Value',
            selector: (row) => row?.redemptionValue || '',
            sortable: true,
            center: true
        },
        {
            name: 'Redemption Date',
            selector: (row) =>
                row?.redeemedAt
                    ? moment(row?.redeemedAt).format('MM-DD-YYYY, h:mm a')
                    : row?.addedToWalletAt
                    ? moment(row?.addedToWalletAt).format('MM-DD-YYYY, h:mm a')
                    : '',
            sortable: true,
            center: true
        },
        {
            name: 'QR Code',
            selector: (row) =>
                row?.qrCode ? <img src={row.qrCode} alt="QR Code" width={50} height={50} style={{ border: '1px solid #ccc', borderRadius: '4px' }} /> : <span>N/A</span>,
            center: true
        },
        {
            name: 'Redeemed Branch',
            selector: (row) => row?.redeemedBranch?.name || '',
            sortable: true,
            center: true
        },
        {
            name: (
                <h6 className="text-capitalize mt-2 text-center">
                    <span className="fw-bold">Action</span>
                </h6>
            ),
            selector: (row) => (
                <div className="d-flex">
                    <div className="view-detail">
                        <OverlayTrigger delay={{ show: 300, hide: 0 }} placement={'bottom'} overlay={<Tooltip id={`tooltip-bottom`}>View details</Tooltip>}>
                            <div className="edit-detail">
                                <img
                                    width={15}
                                    className="me-3"
                                    src={viewIcon}
                                    alt=""
                                    onClick={() => {
                                        setSelectedItem(row);
                                        navigate('/redemptions/redemption-detail', {
                                            state: { redemption_detail: row, redemption_id: row._id, redirect_url: '/redemption-history' }
                                        });
                                    }}
                                />
                            </div>
                        </OverlayTrigger>
                    </div>
                    {user?.role !== 'commuter' && (
                        <div className="del-detail">
                            <OverlayTrigger placement={'bottom'} overlay={<Tooltip id={`tooltip-bottom`}>Delete reward</Tooltip>}>
                                <div className="edit-detail">
                                    <img
                                        width={15}
                                        className="me-3"
                                        src={delIcon}
                                        alt=""
                                        onClick={() => {
                                            setSelectedItem(row);
                                            setShowDelModal(true);
                                        }}
                                    />
                                </div>
                            </OverlayTrigger>
                        </div>
                    )}
                </div>
            ),
            center: true
            // sortable: true
        }
    ];

    const customStyles = {
        rows: {
            style: {
                // minHeight: '72px' // override the row height
                textAling: 'center'
            }
        },
        headCells: {
            style: {
                paddingLeft: '8px', // override the cell padding for head cells
                paddingRight: '8px',
                backgroundColor: '#13aa61',
                color: 'white',
                '&:hover': {
                    cursor: 'pointer'
                }
            }
        }
        // cells: {
        //     style: {
        //         paddingLeft: '8px', // override the cell padding for data cells
        //         paddingRight: '8px'
        //     }
        // }
    };

    const handlePageChange = (pageNo) => {
        fetchRedemptionHistory(pageSize, pageNo);
    };

    const handlePerRowsChange = async (newPageSize, pageNo) => {
        setPageSize(newPageSize);
        fetchRedemptionHistory(newPageSize, pageNo);
    };

    // CSV Export function
    const handleExportCSV = () => {
        if (!redemptionData || redemptionData.length === 0) {
            toast.info('No data to export.');
            return;
        }
        // Prepare data for CSV
        const csvData = redemptionData.map((row) => ({
            'Reward Title': row?.offer?.name || '',
            Branches: Array.isArray(row?.branches)
                ? row.branches
                      .map((b) => b?.name)
                      .filter(Boolean)
                      .join(', ')
                : row?.branch?.name || '',
            ...(user?.role !== 'commuter' && { 'TR Rewards Member': row?.commuter?.name || '' }),
            'Redemption Value': row?.redemptionValue || '',
            'Redemption Date': row?.redeemedAt
                ? moment(row?.redeemedAt).format('MM-DD-YYYY, h:mm a')
                : row?.addedToWalletAt
                ? moment(row?.addedToWalletAt).format('MM-DD-YYYY, h:mm a')
                : '',
            'QR Code': row?.qrCode || ''
        }));
        const csv = Papa.unparse(csvData);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'redemption-history.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div style={{ padding: '20px' }}>
            <Row className="mb-4 mt-1">
                <Col sm={3} md={3} style={{ paddingRight: 0 }}>
                    <input
                        type={'search'}
                        className="search-input-bar"
                        placeholder={'Search here...'}
                        onChange={(e) => {
                            e.preventDefault();
                            searchDebounce(e.target.value);
                        }}
                    />
                </Col>
                {user?.role !== 'commuter' ? (
                    <>
                        <Col sm={1} md={1} style={{ paddingRight: 0 }}>
                            <div className="d-flex justify-content-start my-3 my-sm-0">
                                <select
                                    onChange={(e) => {
                                        setBranchFilter(e.target.value);
                                        setIsBranchSelected(!!e.target.value);
                                    }}
                                    className="status-selector w-120"
                                    style={{ color: !isBranchSelected ? '#9a9a9a' : '' }}
                                    value={branchFilter}
                                >
                                    <option value={''} hidden disabled>
                                        Branch
                                    </option>
                                    <option value={''}>All branches</option>
                                    {branches.map((branch) => (
                                        <option key={branch._id} value={branch._id}>
                                            {branch.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </Col>
                        <Col sm={5} md={5}>
                            <div className="d-flex justify-content-start my-3 my-sm-0">
                                <select
                                    onChange={(e) => {
                                        setCommuterFilter(e.target.value);
                                        setIsCommuterSelected(!!e.target.value);
                                    }}
                                    className="status-selector w-120"
                                    style={{ color: !isCommuterSelected ? '#9a9a9a' : '' }}
                                    value={commuterFilter}
                                >
                                    <option value={''} hidden disabled>
                                        Commuter
                                    </option>
                                    <option value={''}>All commuters</option>
                                    {commuters.map((commuter) => (
                                        <option key={commuter._id} value={commuter._id}>
                                            {commuter.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </Col>
                        <Col sm={3} md={3}>
                            <div className="d-flex gap-10 justify-content-end h-100">
                                {!buttonDisable && (
                                    <Button
                                        className="add-record-btn justify-content-center w-140"
                                        style={{ background: 'red' }}
                                        onClick={() => setShowBulkDel(true)}
                                        disabled={buttonDisable}
                                    >
                                        Delete
                                    </Button>
                                )}
                                <Button
                                    className="add-record-btn justify-content-center w-140 ms-2"
                                    variant="success"
                                    onClick={handleExportCSV}
                                    disabled={redemptionData.length === 0}
                                >
                                    Export to CSV
                                </Button>
                            </div>
                        </Col>
                    </>
                ) : (
                    <>
                        <Col sm={6} md={6}></Col>
                        <Col sm={3} md={3}>
                            <div className="d-flex gap-10 justify-content-end h-100">
                                <Button
                                    className="add-record-btn justify-content-center w-140 ms-2"
                                    variant="success"
                                    onClick={handleExportCSV}
                                    disabled={redemptionData.length === 0}
                                >
                                    Export to CSV
                                </Button>
                            </div>
                        </Col>
                    </>
                )}
            </Row>
            <CommonModal handleClose={handleClose} show={showDelModal} handleDelete={handleDelete} text={'Are you sure you want to delete this redemption history?'} />
            <CommonModal handleClose={handleClose} show={showBulkDel} handleDelete={deleteBulk} text={'Are you sure you want to delete all redemption histories?'} />

            <DataTable
                columns={columns}
                data={redemptionData}
                pagination
                paginationServer
                paginationTotalRows={totalRecords}
                onChangeRowsPerPage={handlePerRowsChange}
                onChangePage={handlePageChange}
                progressPending={loading}
                responsive
                direction="auto"
                fixedHeaderScrollHeight="300px"
                selectableRows={user?.role !== 'commuter'}
                onSelectedRowsChange={handleRowSelected}
                customStyles={customStyles}
            ></DataTable>
        </div>
    );
};

export default RedemptionHistory;
